import { Player } from '../types/user';
import { UserSubscription, DEFAULT_SUBSCRIPTION_LIMITS, SubscriptionPlan, SubscriptionLimits } from '../types/subscription';

export const DEFAULT_SUBSCRIPTION: UserSubscription = {
    plan: 'starter',
    status: 'active',
    startDate: new Date().toISOString(),
    endDate: '2099-12-31T23:59:59.999Z',
    autoRenew: false
};

export const SubscriptionService = {
    getSubscription(user: Player | null): UserSubscription {
        if (!user || !user.subscription) {
            return DEFAULT_SUBSCRIPTION;
        }
        // Handle legacy/corrupt string data
        if (typeof user.subscription === 'string') {
            return {
                ...DEFAULT_SUBSCRIPTION,
                plan: user.subscription === 'club' ? 'pro' : user.subscription
            } as UserSubscription;
        }
        // Handle object with legacy 'club' plan
        if ((user.subscription as any).plan === 'club') {
            return { ...user.subscription, plan: 'pro' };
        }
        return user.subscription;
    },

    getLimits(user: Player | null) {
        const sub = this.getSubscription(user);
        // Map 'club' to 'pro' for limits lookup (extra safety)
        const effectivePlan = (sub.plan as any) === 'club' ? 'pro' : sub.plan;
        return DEFAULT_SUBSCRIPTION_LIMITS[effectivePlan as SubscriptionPlan] || DEFAULT_SUBSCRIPTION_LIMITS['starter'];
    },

    canCreateGroup(user: Player | null, currentOwnedGroupsCount: number, limits?: SubscriptionLimits): { allowed: boolean; reason?: string } {
        const effectiveLimits = limits || this.getLimits(user);
        if (currentOwnedGroupsCount >= effectiveLimits.maxGroups) {
            return {
                allowed: false,
                reason: `Votre offre ${this.getSubscription(user).plan.toUpperCase()} est limitée à ${effectiveLimits.maxGroups} groupe(s).`
            };
        }
        return { allowed: true };
    },

    canAddPlayerToGroup(user: Player | null, currentGroupSize: number, limits?: SubscriptionLimits): { allowed: boolean; reason?: string } {
        // user here is the GROUP OWNER, not the player being added
        const effectiveLimits = limits || this.getLimits(user);
        if (currentGroupSize >= effectiveLimits.maxPlayersPerGroup) {
            return {
                allowed: false,
                reason: `Votre offre ${this.getSubscription(user).plan.toUpperCase()} est limitée à ${effectiveLimits.maxPlayersPerGroup} joueurs par groupe.`
            };
        }
        return { allowed: true };
    },

    canCreateTournament(_user: Player | null, _limits?: SubscriptionLimits): { allowed: boolean; reason?: string } {
        // TEMPORARY AUDIT BYPASS
        return { allowed: true };
    },

    canExportToExcel(user: Player | null, limits?: SubscriptionLimits): { allowed: boolean; reason?: string } {
        const effectiveLimits = limits || this.getLimits(user);
        if (!effectiveLimits.canExportToExcel) {
            return {
                allowed: false,
                reason: "L'export Excel est réservé aux abonnements Pro/Club."
            };
        }
        return { allowed: true };
    },

    getMaxTournamentPlayers(user: Player | null, limits?: SubscriptionLimits): number {
        const effectiveLimits = limits || this.getLimits(user);
        return effectiveLimits.maxTournamentPlayers;
    },

    hasAdvancedStats(user: Player | null, limits?: SubscriptionLimits): boolean {
        const effectiveLimits = limits || this.getLimits(user);
        return effectiveLimits.hasAdvancedStats;
    },

    shouldShowAds(user: Player | null, limits?: SubscriptionLimits): boolean {
        const effectiveLimits = limits || this.getLimits(user);
        return effectiveLimits.hasAds;
    },

    getStatsConfig(user: Player | null, limits?: SubscriptionLimits) {
        const effectiveLimits = limits || this.getLimits(user);
        return effectiveLimits.statsConfig || {
            showElo: effectiveLimits.hasAdvancedStats,
            showBadges: effectiveLimits.hasAdvancedStats,
            showHallOfFame: effectiveLimits.hasAdvancedStats,
            showDetailedStats: effectiveLimits.hasAdvancedStats
        };
    },

    canManageSponsors(user: Player | null, limits?: SubscriptionLimits): { allowed: boolean; reason?: string; max: number } {
        const effectiveLimits = limits || this.getLimits(user);
        const max = effectiveLimits.maxSponsors || 0;

        if (max === 0) {
            return {
                allowed: false,
                reason: "La gestion des sponsors est réservée aux abonnements Premium (1) et Club/Pro (Illimité).",
                max: 0
            };
        }
        return { allowed: true, max };
    },

    canAccessKiosk(user: Player | null, limits?: SubscriptionLimits): { allowed: boolean; reason?: string } {
        const effectiveLimits = limits || this.getLimits(user);
        if (!effectiveLimits.canAccessKioskMode) {
            return {
                allowed: false,
                reason: "Le mode Live TV (Kiosque) est réservé à l'offre Club/Pro."
            };
        }
        return { allowed: true };
    }
};
